import {
    FetchRecordingsSortType,
    RecordingRailType,
} from 'store/recordings/actions.types';
import { RecordingItem } from 'store/recordings/reducer.types';

import {
    BtvEditorial,
    BtvProgramme,
    CdvrRecording,
    ContentType,
    DeeplinkRecommendation,
    EventItemWithServiceInfo,
    EventParentalControlRating,
    NowNextData,
    RBEventParentalControlRating,
    VodEditorial,
} from 'services/data-manager/types';

import { noSeasonName } from 'screens/Detail/components/Seasons/constants.ts';
import { DetailsPageParams } from 'screens/Detail/types';

import { ImageContentType } from 'components/NagraImage/types';

import { RequestConfig } from '../types';
import { RequestStatus } from '../utils';

export type RailsBuilderState = {
    getMainMenuConfigRequest: {
        status: RequestStatus;
        error: any;
        standardMenu: MenuConfigurationItem[];
        kidsMenu: MenuConfigurationItem[];
    };
    templateviewsById: TemplateviewsById;
};

export enum RBTemplates {
    Mobile = 'Mobile', // Root RB template for mobile app.
    Kids = 'Kids Mobile', // Root RB template for kids profile.
    Menu = 'Menu Mobile', // Bottom tab menu template.
    MenuKids = 'Menu Kids Mobile', // Bottom tab menu templates for kids profile.
    Home = 'Home Mobile', // Bottom tab Home page.
    HomeKids = 'Home Kids Mobile', // Bottom tab Home page for kids profile.
    Catchup = 'Catchup Mobile', // Bottom tab Catchup page.
    TV = 'TV Mobile', // Bottom tab TV page.
    Recordings = 'Recordings Mobile', // Bottom tab Recordings page.
    OnDemand = 'OnDemand Mobile',

    Settings = 'Settings Mobile', // Settings page.
    SettingsKids = 'Settings Kids Mobile', // Settings page for kids profile.
    Language = 'Language Mobile', // Language settings option.
    Parental = 'PIN Mobile', // Parental settings option.
    Quota = 'Recording Quota Mobile', // Recording Quota settings option.
    InternetConnection = 'Internet connection Mobile', // Internet Connection settings option.
    ChannelLineup = 'Channel lineup Mobile', // Channel Lineup settings option.
    Subscriptions = 'My subscriptions Mobile', // My subscriptions settings option.
    EpgDisplay = 'Display Mobile', // EPG Display settings option.
    AudioSubtitles = 'Audio & Subtitles Mobile', // Audio & Subtitles settings option.
    AudioSubtitlesKids = 'Audio & Subtitles Kids Mobile', // Audio & Subtitles settings option for kids profile.
    DeleteHistory = 'Delete History', // Delete History settings option.
    Consents = 'Consents Mobile', // Consents settings option.
    SpotlightVideo = 'Spotlight video Mobile', // Spotlight video settings option.
    About = 'About Mobile', // About settings option.
    AboutKids = 'About Kids Mobile', // About settings option for kids profile.
    BackendInfo = 'Backend information Mobile', // Backend info settings option.
    BackendInfoKids = 'Backend information Kids Mobile', // Backend info settings option for kids profile.
    TermsConditions = 'Terms & Conditions Mobile', // Terms & Conditions settings option.
    Help = 'Help Mobile', // Help settings option.
    HelpKids = 'Help Kids Mobile', // Help settings option for kids profile.
    SignOut = 'Log out Mobile', // Log out settings option.
    SignOutKids = 'Log out Kids Mobile', // Log out settings option kids profile.

    Search = 'Search Mobile', // Search screen.
}

export enum RBTemplateviewTargets {
    'mobile' = 'mobile',
    'tablet' = 'tablet',
    'tv' = 'tv',
    'desktop' = 'desktop',
}

export enum RBRailSectionType {
    ChannelItem = 'ChannelItem',
    NodeItem = 'NodeItem',
    RecommendationItem = 'RecommendationItem',
    ContentItem = 'ContentItem',
    NonContentItem = 'NonContentItem',
    Deeplink = 'Deeplink',
    DynamicContentGroupItem = 'DynamicContentGroupItem',
    EventItem = 'EventItem',
    ContinueWatchingItem = 'ContinueWatchingItem',
}

export enum RBAssetType {
    EDITORIAL = 'editorial',
    PROGRAMME = 'programme',
    SERIES = 'series',
}

export enum PlaybackTypes {
    live_stcu_event = 'live-stcu-event',
    vod_ltcu = 'vod-ltcu',
    vod_stcu = 'vod-stcu',
    vod_ed = 'vod-ed',
    npvr_event = 'npvr_event',
}

export type TemplateviewsById = Record<
    RBHierarchyTemplateviewItem['id'],
    RequestConfig<RBTemplateview | undefined>
>;

export enum MdsCallKeys {
    OTV_STCU = 'otv_stcu',
    NOW_NEXT = 'now_next',
    CHANNELS = 'channels',
    SEARCH = 'search',
    RECORDINGS = 'recordings',
}

type RBRailCustomProperties = {
    ContentType?: ContentType;
    MDS_Call?: MdsCallKeys;
    limit?: string; // Must be a number, but RB delivers only strings as a custom properties.
    visible?: string; // Must be a boolean, but RB delivers only strings as a custom properties.
    RailType?: RecordingRailType;
    Sorting?: FetchRecordingsSortType;
    filterLastHours?: string; // Used to filter recently added recordings
};

export type RBDeviceType = 'IOS' | 'Android' | 'Browser';

export enum TechnicalDeviceType {
    ios = 'IOS',
    android = 'Android',
    web = 'Browser',
    default = 'IOS',
}

export type ClientBlackoutControlFlags = {
    clientGlobalBlackoutControl?: '0' | '1'; // '0' - disabled, '1' - enabled
    clientMobileBlackoutControl?: '0' | '1'; // '0' - enabled, '1' - disabled
    clientWebBlackoutControl?: '0' | '1'; // '0' - enabled, '1' - disabled
};

export type ClientRestartControlFlags = {
    clientMobileRestartControl?: '0' | '1'; // '0' - disabled, '1' - enabled
    clientWebRestartControl?: '0' | '1'; // '0' - disabled, '1' - enabled
};

export type ClientTimeshiftControlFlags = {
    clientTimeshiftControl?: '0' | '1'; // '0' - disabled, '1' - enabled
};

export type ClientNpvrControlFlags = {
    clientMobileNpvrControl?: '0' | '1'; // '0' - disabled, '1' - enabled
    clientWebNpvrControl?: '0' | '1'; // '0' - disabled, '1' - enabled
};

type ChannelClientTrickModeControlFlags = {
    clientLiveTrickModeControl?: '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7';
    clientVODTrickModeControl?: '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7';
    clientRecordingTrickModeControl?:
        | '0'
        | '1'
        | '2'
        | '3'
        | '4'
        | '5'
        | '6'
        | '7';
};

export type AssetClientTrickModeControlFlags = {
    clientLiveTrickModeControl?: '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7';
    clientVODTrickModeControl?: '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7';
    clientRecordingTrickModeControl?:
        | '0'
        | '1'
        | '2'
        | '3'
        | '4'
        | '5'
        | '6'
        | '7';
};

export type OOHControlFlags = {
    clientMobileLiveOOHStreamingControl?: '0' | '1'; // '0' - streaming disabled, '1' - enabled
    clientMobileRestartOOHControl?: '0' | '1'; // '0' - streaming disabled, '1' - enabled
    clientMobileOOHCatchupControl?: '0' | '1'; // '0' - streaming disabled, '1' - enabled
    clientMobileOOHNpvrControl?: '0' | '1'; // '0' - streaming disabled, '1' - enabled
    clientMobileVODOOHControl?: '0' | '1'; // '0' - streaming disabled, '1' - enabled
};

export type RBChannel = {
    locale: string;
    editorial: {
        id: string;
        period: {
            duration: number;
            start: number;
            end: number;
        };
        companyId: string;
        tvChannel: number;
        catchUpSupport: boolean;
        startOverSupport: boolean;
        networkPvrSupport: boolean;
        Title: null | string;
        ShortName: null | string;
        longName: string;
        Description: string;
        Categories: string[];
        channelGroupRefs: string[];
        PromoImages: string[];
        InHomeBlockingRequired?: string; // "true" || "false",
        guardTime?: number; // Not in use in the application.
        guardTimeEnd?: number; // This is only used to provide extra time at the end of the program.
        Ratings: RBRating[]; // The list of parental rating on channel level
        isMandatoryContentProtection?: boolean;
        nPvrSupport?: 'true' | 'false'; // "true" || "false",
        OTTSubtitleType?: 'text' | 'image';
        bingeWatchingTimeout?: string; // in seconds
        enableBingeWatching?: 'true' | 'false'; // "true" || "false",
    } & ClientBlackoutControlFlags &
        ClientRestartControlFlags &
        ClientTimeshiftControlFlags &
        ChannelClientTrickModeControlFlags &
        ClientNpvrControlFlags &
        OOHControlFlags;
    technical: {
        id: string;
        tvChannel: number;
        period: null;
        catchUpSupport: boolean;
        startOverSupport: boolean;
        StartOverLocation: string;
        networkPvrSupport: boolean;
        networkPvrParameters: null;
        profileRefs: string[];
        PromoImages: string[];
        Title: null | string;
        ShortName: null | string;
        longName: string;
        Description: string;
        NetworkLocation: string;
        products: any[];
        productRefs?: string[];
        drmId?: string;
        isFreeToAir?: boolean;
        timeshiftAllowed?: string; // "true" || "false"
        trickmodeAllowed?: string; // "true" || "false"
        fastforwardAllowed?: string; // "true" || "false"
        rewindAllowed?: string; // "true" || "false"
        streamTimeFormat?: '1' | '2';
        isMandatoryContentProtection?: boolean;
        nPvrSupport?: 'true' | 'false'; // "true" || "false",
    };
    isFreemium: boolean;
    deviceType: RBDeviceType[];
};

type RBPeriod = {
    duration: number | null;
    start: number | null;
    end: number | null;
};

export type NodeLink = {
    id: string;
    publishToEndUser: boolean;
    nodeRefs: string[];
    editorialId: string;
};

export enum ProductType {
    SUBSCRIPTION = 'subscription',
    TRANSACTION = 'transactional',
}

export type RBTechnical = {
    id: string;
    created: number;
    contentGroups: any[];
    nodeRefs: any[];
    mainContentRef: string;
    period: RBPeriod;
    companyId: string;
    profileRef: string;
    profileRefs: string[];
    title: string;
    duration: number;
    media: {
        AV_PlaylistName: {
            id: null | string;
            fileName: string;
            drmId: undefined | string;
            drmInstanceName: null | string;
            format: {};
            uri: string;
        };
    };
    drmId: string;
    drmInstanceName: string;
    qmWatermark: boolean;
    isSTCU: boolean;
    isLTCU: boolean;
    programmeId: string;
    provider: string;
    deviceType: string[];
    promoRefs: any[];
    CUStartDate: number;
    CUEndDate: number;
    Categories: any[];
    PromoImages: any[];
    Actors: any[];
    Directors: any[];
    Producers: any[];
    Writers: any[];
    Subtitles: any[];
    Audio: any[];
    ServiceLongName: string;
    ProgrammeEndDate: string | number | undefined;
    GuardTimeStart: string;
    ServiceId: string;
    GuardTimeEnd: string;
    ProgramId: string;
    ProgrammeStartDate: string | number | undefined;
    products: PurchaseProduct[];
    Definition: string;
    JITPValue: string;
};

export type PurchaseProduct = {
    startPurchase: number;
    endPurchase: number;
    price: {
        startPurchase: number;
        endPurchase: number;
        currency: string;
        value: number;
    };
    billingModel: {
        period: number;
    };
    startValidity: number;
    endValidity: number;
    deviceClass: string[];
    type: ProductType;
    title: string;
    isFreemium: boolean;
    technicalTypes: string[];
    link: {
        id: string;
        start: number;
        end: number;
    };
    Description: string;
    Title: string;
    id: string;
    rentalDuration?: number;
};

export type RBEditorial = {
    id: string;
    created: number;
    contentGroups: any[];
    nodeRefs: string[];
    contentType: string;
    period: RBPeriod;
    seasonRef: string;
    seriesRef: string;
    companyId: string;
    profileRef: string;
    profileRefs: string[];
    title: string;
    episodeNumber: number;
    EpisodeTitle: string;
    clientControls: {};
    isSTCU: boolean;
    isLTCU: boolean;
    provider: string;
    deviceType: any[];
    promoRefs: any[];
    nodeLinks: NodeLink[];
    displayPriority: {};
    CUStartDate: number;
    CUEndDate: number;
    Categories: string[];
    Genre: string[];
    PromoImages: string[];
    Actors: string[];
    Directors: any[];
    Producers: any[];
    Writers: any[];
    SeasonNumber: number;
    Subtitles: any[];
    Subtitle: string;
    Audio: any[];
    SeriesId: string;
    AudioMode: string;
    ModelType: string;
    Qualifiers: string;
    Countries: string;
    SeasonId: string;
    SeriesTitle: string;
    Description: string;
    Advisory: string;
    Episode: string;
    Title: string;
    duration: number;
    Ratings: RBRating[];
    technicals: RBTechnical[];
    playbackType: PlaybackTypes;
    NetDuration?: number;
} & ClientBlackoutControlFlags &
    ClientRestartControlFlags &
    ClientTimeshiftControlFlags &
    AssetClientTrickModeControlFlags &
    OOHControlFlags;

export type RBRating = {
    code?: RBEventParentalControlRating;
    precedence?: 1 | 6 | 12 | 18 | 100 | 101;
    Title: string;
    ratingBody: 'HCRS';
};

export type RBProgramEditorialTechnical = {
    _id: string;
    id: string;
    created: number;
    contentGroups: any[];
    nodeRefs: any[];
    mainContentRef: string;
    period: {
        start: number;
        end: number;
        duration: number;
    };
    companyId: string;
    profileRef: string;
    profileRefs: string[];
    title: string;
    media: {
        AV_PlaylistName: {
            fileName: string;
            uri: string;
            format: {};
        };
    };
    isSTCU: boolean;
    isLTCU: boolean;
    provider: string;
    deviceType: TechnicalDeviceType[];
    locale: string;
    CUStartDate: number;
    CUEndDate: number;
    Categories: string[];
    PromoImages: string[];
    Actors: string[];
    Directors: string[];
    Producers: string[];
    Writers: string[];
    Subtitles: string[];
    Audio: string[];
    GuardTimeEnd: string;
    GuardTimeStart: string;
    ServiceId: string;
    ProgrammeEndDate: string | number;
    ServiceLongName: string;
    ProgramId: string;
    ProgrammeStartDate: string | number;
    nPvrSupport?: 'true' | 'false'; // "true" || "false",
};

export type MixedItem = {
    id: string;
    type: RBAssetType | undefined;
    contentType: ImageContentType | string | null | undefined;
    playbackType: PlaybackTypes | undefined;
    title: string;
    channelId: string | undefined;
    seriesId: string | undefined;
    season: string | number | undefined;
    episode: string | number | undefined;
    episodeTitle: string | undefined;
    startTime: number | undefined;
    endTime: number | undefined;
    year: number | undefined;
    parentalRating: EventParentalControlRating | undefined;
    CUEndDate: number | undefined;
    isCatchUp: boolean | undefined;
    isStartOver: boolean | undefined;
    links: RBLink[] | undefined;
    countries?: string | undefined;
    genres?: string[] | undefined;
    detailsPageParams?: DetailsPageParams;
    vodProductIds?: (string | undefined)[] | undefined;
    vodStartTime?: number | undefined;
    position?: number | undefined;
    duration?: number | undefined;
};

export type RBProgram = {
    editorial?: {
        Title: string;
        technicals?: RBTechnical[];
        id: string;
        title: string;
        Actors: string[];
        Rating: RBRating | null;
        Ratings: RBRating[] | null;
        duration: number | null;
        Description: string;
        Synopsis: string | null;
        Categories: string[];
        Directors: string[];
        Producers: string[];
        seriesRef?: string;
        SeriesTitle: string | null;
        TmsId: string | null;
        SeasonNumber?: string | number | null;
        episodeNumber?: string | number | null;
        Episode?: null | string;
        EpisodeTitle?: string | null;
        Year?: number | null;
        Images?: string[] | null;
        position: number;
        sportType: null;
        ltcuRepresentationOfStcu: boolean;
        PromoImages: string[];
        nodeRefs: string[];
        DisplayPriority: {};
        InHomeBlockingRequired: boolean;
        period: {
            duration: number;
            start: number | null;
            end: number;
        };
        CUStartDate: number | null;
        CUEndDate: number | null;
        type: string;
        Countries?: string | null;
        contentType: string | null;
        nPvrSupport?: 'true' | 'false'; // "true" || "false",
        companyId?: string;
    };
    id: string;
    eventId: string;
    SeriesId: string | null;
    seriesRef?: string;
    SeriesTitle: string | null;
    contentRef: string;
    serviceRef: string;
    isnPvr: boolean;
    nPvrSupport: 'true' | 'false'; // "true" || "false",
    isStartOver: boolean;
    isCatchUp: boolean;
    isPPV: boolean;
    period: {
        duration: number;
        start: number;
        end: number;
    };
    title: string;
    Title: string;
    isFreemium: boolean;
    CUStartDate: null | number;
    CUEndDate: null | number;
    Description: string;
    Synopsis: string | null;
    Rating: RBRating | null;
    Ratings: RBRating[] | null;
    links: RBLink[];
    duration: number | null;
    type: RBAssetType;
    Year?: number | null;
    Categories?: string | null;
    Genre?: string[];
    isSTCU: boolean;
    isLTCU: boolean;
    SeasonNumber?: string | null;
    episodeNumber?: string | null;
    EpisodeTitle?: string | null;
    Episode?: string | null;
    Countries?: string | null;
    Subtitle?: string | null;
    Subtitles?: string[] | null;
    Audio?: string[] | null;
    airingStartTime?: number;
    airingEndTime?: number;
    playbackType: PlaybackTypes;
} & ClientBlackoutControlFlags &
    AssetClientTrickModeControlFlags &
    ClientTimeshiftControlFlags &
    ClientRestartControlFlags &
    ClientNpvrControlFlags &
    OOHControlFlags;

export type EpgProgram = Pick<
    RBProgram,
    | 'id'
    | 'SeriesId'
    | 'seriesRef'
    | 'SeriesTitle'
    | 'contentRef'
    | 'serviceRef'
    | 'isStartOver'
    | 'isCatchUp'
    | 'period'
    | 'title'
    | 'Title'
    | 'CUStartDate'
    | 'CUEndDate'
    | 'Description'
    | 'Rating'
    | 'Ratings'
    | 'links'
    | 'type'
    | 'Genre'
    | 'isSTCU'
    | 'isLTCU'
    | 'SeasonNumber'
    | 'EpisodeTitle'
    | 'episodeNumber'
    | 'Episode'
    | 'Countries'
    | 'airingStartTime'
    | 'isnPvr'
    | 'nPvrSupport'
> &
    ClientBlackoutControlFlags &
    ClientRestartControlFlags &
    ClientTimeshiftControlFlags &
    ClientNpvrControlFlags &
    AssetClientTrickModeControlFlags &
    OOHControlFlags & {
        editorial: {
            technicals?: RBTechnical[];
            id: string;
            title: string;
            Ratings: RBRating[] | null;
            Description: string;
            seriesRef?: string;
            SeasonNumber?: number | null;
            episodeNumber?: number | null;
            Images?: string[] | null;
            PromoImages: string[];
            CUStartDate: number | null;
            CUEndDate: number | null;
            type: string;
            Countries?: string | null;
            contentType: string | null;
            nPvrSupport?: 'true' | 'false'; // "true" || "false",
        };
        testID?: string;
    };

export type RBRailSection = {
    type: RBRailSectionType;
    properties: Record<string, string>;
    contents:
        | null[]
        | BtvEditorial[]
        | BtvProgramme[]
        | DeeplinkRecommendation[]
        | VodEditorial[]
        | EventItemWithServiceInfo[]
        | RBProgram[]
        | RecordingItem[];
};

export type RBRail = {
    id: string;
    name: string;
    title: string | null;
    layout: string;
    properties: RBRailCustomProperties;
    sections: RBRailSection[];
    hasNext: boolean;
};

export type RBParsedRail = Omit<RBRail, 'sections'> & {
    sections:
        | MixedItem[]
        | CdvrRecording[]
        | NowNextData[]
        | RBChannel[]
        | RBProgram[]
        | SearchProgram[]
        | CastPerson[]
        | RecordingItem[];
};

export type RBRootHierarchyTemplateview = {
    id: string;
    name: RBTemplates;
    children: RBRootTemplateview<RBRootTemplateview>[];
    properties: Record<string, string>;
};

export type RBHierarchyTemplateviewItem = {
    id: string;
    tenant: string;
    name: RBTemplates;
    title: string;
    properties: {};
    children: RBHierarchyTemplateviewItem[];
};

export type RBRootTemplateview<T = string> = {
    id: string;
    name: RBTemplates;
    parent?: string;
    children: T[];
    properties: Record<string, string>;
};

export enum MenuItemType {
    MODULAR = 'MODULAR',
    EPG = 'EPG',
    SEARCH = 'SEARCH',
    SETTINGS = 'SETTINGS',
}

export type MenuConfigurationItem = {
    id: RBHierarchyTemplateviewItem['id'];
    name: RBHierarchyTemplateviewItem['name'];
    type: MenuItemType | undefined;
    children?: MenuConfigurationItem[];
    POEditorKey: string;
    icon: string;
    testID?: string;
    accessibilityLabel?: string;
};

export type RBBottomTabMenuItem = {
    id: RBHierarchyTemplateviewItem['id'];
    name: RBHierarchyTemplateviewItem['name'];
    POEditorKey: string;
    icon: string;
    testID?: string;
};

export type RBTemplateview<R = RBParsedRail[]> = {
    name: RBTemplates;
    title: string | null;
    targets: RBTemplateviewTargets[];
    properties: Record<string, string>;
    rails: R;
    testID?: string;
};

export type RBTemplateviewSummary = {
    name: RBTemplates;
    title: string | null;
    targets: RBTemplateviewTargets[];
    properties: Record<string, string>;
    rails: RBRail[];
};

export interface BTVServicesResponse {
    /**
     * The number of records, in total, that satisfy your query
     * @example 1
     */
    total_records?: number;
    services?: RBChannel[];
}

export interface BTVProgrammesResponse {
    /**
     * The number of records, in total, that satisfy your query
     * @example 1
     */
    total_records: number;
    programmes: RBProgram[];
}

export interface VodResponse {
    total_records: number;
    version: number;
    editorials: VodEditorial[];
}

export interface SeasonsListResponse {
    values: (number | string)[];
    version: string;
}

type RecordingsSeason = {
    seasonId: string;
    seasonNumber: number | typeof noSeasonName;
    totalEpisodes: number;
};

export type RecordingsSeasonsResponse = {
    seasons: RecordingsSeason[];
    totalEpisodes: number;
    totalSeasons: number;
};

export type ResponseError = {
    error: {
        code?: string;
        message?: string;
    };
};

export type RBLink = {
    id: string;
    ServiceId?: string;
    contentRef: string;
    period: {
        duration: number;
        start: number;
        end: number;
    };
    provider: string;
    deviceType: string[];
    original: boolean;
    type: string;
    action: string;
    url: string;
    playbackOptions: [
        {
            price: number;
            definition: string;
            currency: string;
            license: string;
        },
    ];
    playbackParams: string;
};

export type SearchDeeplink = {
    id: string;
    title: string;
    locale: string;
    source: string;
    description: string;
    actors: string[];
    categories: string[];
    genres: string[];
    devices: string[];
    synopsis: string;
    providers: string[];
    series: string;
    season: string;
    rating: EventParentalControlRating;
    Ratings: RBRating[];
    links: SearchLink[];
    type: 'vod' | 'stcu' | 'series' | 'season' | 'programme';
};

export type SearchLink = {
    period: {
        duration: number;
        start: number;
        end: number;
    };
    uri: string;
    url: string;
    id: string;
    playbackParams: string;
    provider: string;
    contentRef: string;
    title: string;
    type: string;
    action: string;
    original: boolean;
};

export type SearchProgram = {
    id: string;
    title: string;
    type: 'vod' | 'stcu' | 'series' | 'season' | 'programme';
    contentType?: string;
    source: string;
    locale: string;
    episode?: string;
    episodeNumber?: number;
    description: string;
    year: number;
    actors: string[];
    categories: string[];
    devices: string[];
    providers: string[];
    channel: string;
    channelName: string;
    series: string;
    season?: string;
    seasonNumber?: number;
    start: number;
    end: number;
    rating: EventParentalControlRating;
    links: RBLink[];
} & ClientBlackoutControlFlags &
    OOHControlFlags;

export type SearchFacadeResponse = {
    results: SearchProgram[];
    totalRecords: number;
};

export type GetRecordingsResponse = {
    recordingSet: RecordingItem[];
    totalRecords: number;
};

export type LocalizedName = {
    locale: 'de_DE' | 'en_GB';
    name: string;
};

export enum Roles {
    ACTOR = 'Actor',
    DIRECTOR = 'Director',
}

export type Playlist = {
    role: Roles[];
    id: string;
};

export type CastCrewPerson = {
    names: LocalizedName[];
    images: string[];
    id: string;
    type: 'cast';
    playlist: Playlist[];
};

export type CastPerson = {
    name: string;
    id: string;
    image?: string;
};

export interface CastCrewResponse {
    /**
     * The number of records, in total, that satisfy your query
     * @example 1
     */
    total_records: number;
    castCrewPersonas: CastCrewPerson[];
}

export type NowNextResponse = {
    results: NowNextData[];
};
